import React, { useEffect, useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DropdownMenu, Submenu, type MenuCloseReason } from "./DropdownMenu";

export interface ContextMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /// Muted trailing text. Carries the reason an item is unavailable, which a
  /// hover tooltip can't do inside a menu you're already hovering.
  hint?: string;
  danger?: boolean;
  divider?: boolean;
  /// Nested items in their own panel beside this one, the way the details
  /// panel's Export button opens its own menu. The row toggles that panel
  /// instead of running `onClick`.
  submenu?: ContextMenuItem[];
}

interface ContextMenuProps {
  items: ContextMenuItem[];
  position: { x: number; y: number } | null;
  onClose: (reason: MenuCloseReason) => void;
  /// Names the menu for screen readers, which otherwise announce it unlabelled —
  /// "Actions for Markets", not "Actions".
  ariaLabel: string;
}

const PANEL_STYLE = { minWidth: "var(--sidebar-dropdown-min-width)" };

const MenuRow: React.FC<{
  item: ContextMenuItem;
  active?: boolean;
  onSelect: () => void;
  onPointerEnter?: () => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}> = ({ item, active = false, onSelect, onPointerEnter, onKeyDown }) => (
  <button
    type="button"
    role="menuitem"
    tabIndex={-1}
    // `aria-disabled` rather than `disabled`: an unavailable action stays
    // focusable, so arrowing through the menu still shows it exists.
    aria-disabled={item.disabled || undefined}
    aria-haspopup={item.submenu ? "menu" : undefined}
    aria-expanded={item.submenu ? active : undefined}
    onClick={item.disabled ? undefined : onSelect}
    onPointerEnter={onPointerEnter}
    onKeyDown={onKeyDown}
    className={`w-full flex items-center px-3 py-2 text-left ${
      item.disabled
        ? "text-fg-muted-medium cursor-default opacity-40"
        : item.danger
          ? "text-error-regular focus:bg-list-hover cursor-pointer"
          : "text-fg-muted-regular focus:bg-list-hover cursor-pointer"
    } ${active ? "bg-list-hover" : ""}`}
  >
    {item.icon && <div className="mr-2 flex items-center">{item.icon}</div>}
    <span className="truncate grow">{item.label}</span>
    {item.hint && (
      <span className="text-fg-muted-regular-small ml-3 shrink-0">
        {item.hint}
      </span>
    )}
    {item.submenu && <ChevronRightIcon className="icon-sm ml-2 shrink-0" />}
  </button>
);

const MenuDivider: React.FC = () => (
  <div role="separator" className="border-t border-border my-1" />
);

/** A `DropdownMenu` at a point, drawn from item data. */
export const ContextMenu: React.FC<ContextMenuProps> = ({
  items,
  position,
  onClose,
  ariaLabel,
}) => {
  const [openSubmenu, setOpenSubmenu] = useState<number | null>(null);
  const [submenuTakesFocus, setSubmenuTakesFocus] = useState(false);

  useEffect(() => {
    setOpenSubmenu(null);
    setSubmenuTakesFocus(false);
  }, [position]);

  const select = (item: ContextMenuItem) => () => {
    item.onClick();
    onClose("select");
  };

  return (
    <DropdownMenu
      at={position}
      isOpen={position !== null}
      onClose={onClose}
      label={ariaLabel}
      style={PANEL_STYLE}
    >
      {items.map((item, index) =>
        item.divider ? (
          <MenuDivider key={index} />
        ) : item.submenu && !item.disabled ? (
          <div key={index} className="relative">
            <MenuRow
              item={item}
              active={openSubmenu === index}
              onSelect={() =>
                setOpenSubmenu((cur) => (cur === index ? null : index))
              }
              onPointerEnter={() => setOpenSubmenu(index)}
              onKeyDown={(event) => {
                if (event.key !== "ArrowRight") return;
                event.preventDefault();
                setOpenSubmenu(index);
                setSubmenuTakesFocus(true);
              }}
            />
            {openSubmenu === index && (
              <Submenu
                label={item.label}
                autoFocus={submenuTakesFocus}
                onDismiss={() => {
                  setOpenSubmenu(null);
                  setSubmenuTakesFocus(false);
                }}
                style={PANEL_STYLE}
              >
                {item.submenu.map((sub, subIndex) =>
                  sub.divider ? (
                    <MenuDivider key={subIndex} />
                  ) : (
                    <MenuRow key={subIndex} item={sub} onSelect={select(sub)} />
                  ),
                )}
              </Submenu>
            )}
          </div>
        ) : (
          <MenuRow
            key={index}
            item={item}
            onSelect={select(item)}
            onPointerEnter={() => setOpenSubmenu(null)}
          />
        ),
      )}
    </DropdownMenu>
  );
};
